import { abbrevRepo } from '../../data/repositories';

/**
 * Abbreviation expansion: 2–3 letter codes become whole sentences.
 *
 * Ships in Phase 1 while word prediction waits for Phase 2, and that ordering is deliberate. It
 * is deterministic, needs no lexicon, and — unlike a prediction row — imposes **no visual search
 * cost at all**. For someone whose visual impairment we cannot measure, that may make it
 * outperform prediction outright (§5.3b), so it is the cheaper win and it ships first.
 *
 * Rules, all narrow on purpose. A surprise expansion mid-sentence is worse than no expansion,
 * because the person then has to notice it, undo it, and retype:
 *
 *  - only on a **word boundary** — the code must be a whole word;
 *  - **case-insensitive match, expansion used verbatim**, so `ffd` and `FFD` both work and the
 *    stored sentence keeps its capitals;
 *  - the code must be the text immediately before the boundary, never anywhere earlier.
 */

export interface Expansion {
  /** The code that matched, for the confirmation highlight. */
  code: string;
  expansion: string;
  /** Index in the buffer where the code started. */
  startIndex: number;
  /** Buffer with the code replaced. */
  text: string;
  /** Where the caret belongs afterwards. */
  caret: number;
}

/** Word characters for boundary purposes. Mirrors the composer's definition. */
const WORD_CHAR = /[\p{L}\p{N}\-']/u;

/**
 * Find the code immediately before `caret`, skipping the boundary character the person just typed.
 *
 * Exported for tests: the boundary rules are the whole substance of this feature.
 */
export function codeBeforeCaret(text: string, caret: number): { code: string; startIndex: number } | null {
  let end = caret;

  // The word must actually be **finished**. If the character before the caret is still a word
  // character, the person may be mid-word — `gwa` could be the start of something longer — and
  // expanding now would replace a word they had not yet written.
  //
  // Enforced here rather than trusted to callers: today the only caller fires on space, but this
  // function puts whole sentences into someone's mouth, so it defends its own rule.
  if (end === 0 || WORD_CHAR.test(text[end - 1] ?? '')) return null;

  // Step back over exactly one trailing boundary character — the space or punctuation that
  // triggered the attempt.
  end -= 1;
  if (end === 0) return null;

  let start = end;
  while (start > 0 && WORD_CHAR.test(text[start - 1] ?? '')) start -= 1;

  const code = text.slice(start, end);
  if (code === '') return null;
  return { code, startIndex: start };
}

/**
 * Pure expansion, given a code table. `expandAbbrev` below is the wired version.
 *
 * Returns null when nothing matched — which must stay the common case, because an unexpected
 * expansion costs the person more than a missing one.
 */
export function applyExpansion(
  text: string,
  caret: number,
  table: ReadonlyMap<string, string>,
): Expansion | null {
  const found = codeBeforeCaret(text, caret);
  if (!found) return null;

  const expansion = table.get(found.code.toLowerCase());
  if (expansion === undefined) return null;

  const before = text.slice(0, found.startIndex);
  const after = text.slice(found.startIndex + found.code.length);

  return {
    code: found.code,
    expansion,
    startIndex: found.startIndex,
    text: before + expansion + after,
    caret: found.startIndex + expansion.length,
  };
}

/** Codes are stored as typed; matching folds case so a stray capital still expands. */
export async function loadAbbrevTable(): Promise<Map<string, string>> {
  const rows = await abbrevRepo.all();
  return new Map(rows.map((row) => [row.code.toLowerCase(), row.expansion]));
}

export async function expandAbbrev(text: string, caret: number): Promise<Expansion | null> {
  try {
    return applyExpansion(text, caret, await loadAbbrevTable());
  } catch (err) {
    // A storage failure must never break typing.
    console.error('[abbrev] lookup failed', err);
    return null;
  }
}
